import { createFileRoute, Link } from "@tanstack/react-router";
import danceImg from "@/assets/dj-mariage-dance.jpg";
import sparksImg from "@/assets/dj-mariage-sparks.jpg";
import { useReveal } from "@/hooks/use-reveal";
import { PageHero } from "@/components/page-hero";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
export const Route = createFileRoute("/soirees-evenements/")({
  head: () => ({
    meta: [
      { title: "DJ Soirées & Événements Privés Vaucluse | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("DJ Val anime anniversaires, soirées privées, événements d'entreprise et baptêmes dans le Vaucluse et en Provence. Sono et éclairage LED inclus, devis en 24 h.")
      },
      { property: "og:title", content: "DJ Soirées & Événements Privés Vaucluse | DJ Val" },
      {
        property: "og:description",
        content:
          metaDesc("Anniversaires, soirées d'entreprise, baptêmes : ambiance festive garantie en Provence et Vaucluse.")
      },
      { property: "og:url", content: "https://dj-val.fr/soirees-evenements" },
      { property: "og:image", content: `https://dj-val.fr${danceImg}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://dj-val.fr${danceImg}` },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/soirees-evenements" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "DJ Soirées & Événements Privés",
          serviceType: "Animation DJ pour anniversaires, soirées d'entreprise, baptêmes",
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          areaServed: ["Cavaillon", "Avignon", "Vaucluse", "Provence-Alpes-Côte d'Azur"],
        }),
      },
    ],
  }),
  component: Page,
});

const TYPES = [
  {
    title: "Anniversaires",
    text: "Des 18 ans aux 70 ans, une playlist sur mesure pour traverser toutes les générations et faire danser tous vos invités jusqu'au bout de la nuit.",
  },
  {
    title: "Soirées d'entreprise",
    text: "Séminaire, soirée de fin d'année, lancement produit. Animation discrète au cocktail, montée en puissance pour la soirée dansante.",
  },
  {
    title: "Baptêmes & communions",
    text: "Une ambiance familiale et chaleureuse, adaptée aux enfants comme aux grands. Animation micro et sonorisation soignée.",
  },
  {
    title: "Camping & événements estivaux",
    text: "Soirées plein air en camping, villages de vacances et événements saisonniers. Matériel résistant et programmation festive.",
  },
  {
    title: "Soirées privées & cocktails",
    text: "Vernissage, crémaillère, soirée VIP. Sélection lounge en début de soirée, club en fin de nuit.",
  },
  {
    title: "Événements associatifs",
    text: "Galas, soirées caritatives, lotos. Tarifs adaptés et coordination avec les organisateurs.",
  },
];

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={danceImg}
        eyebrow="DJ Soirées & Événements"
        title={<><span className="italic">Soirées privées</span><span className="block">& événements dans le Vaucluse</span></>}
        subtitle="De l'anniversaire intime à la soirée d'entreprise grand format, DJ Val crée une ambiance sur mesure pour rendre votre événement inoubliable."
        primary={{ label: "Devis gratuit", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:0767908667" }}
        height="regular"
      />
      <ReviewsProof />

      {/* TYPES */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-foreground">Nos prestations</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Tous types d'événements
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-foreground/10 md:grid-cols-2 lg:grid-cols-3 border border-foreground/10">
          {TYPES.map((t, i) => (
            <div
              key={t.title}
              className="bg-background p-10 sr hover:bg-card transition-colors"
              data-reveal
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <p className="font-serif-display italic text-foreground text-2xl">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{t.title}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INCLUDED */}
      <section
        className="relative py-24 px-6 bg-cover bg-center bg-fixed border-y border-foreground/10"
        style={{ backgroundImage: `url(${sparksImg})` }}
      >
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative max-w-3xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-foreground">Inclus dans chaque prestation</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            Une animation <span className="italic text-foreground">clé en main</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <ul className="grid sm:grid-cols-2 gap-4 text-left text-sm text-muted-foreground">
            {[
              "Sonorisation professionnelle adaptée à la salle",
              "Éclairage LED & jeux de lumière",
              "Micro HF pour discours et animations",
              "Playlist personnalisée selon vos goûts",
              "Installation & démontage par nos soins",
              "Coordination avec les autres prestataires",
            ].map((x) => (
              <li key={x} className="flex gap-3">
                <span className="text-foreground">✦</span>
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/contactez-nous"
            className="mt-10 inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
          >
            Réserver votre date
          </Link>
        </div>
      </section>
    </div>
  );
}
